import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOffIcon from "@mui/icons-material/EditOff";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import GrantRow, { GrantRowTable } from "permissions/_views/grant-row/grant-row";
import { buildGrantRow, grantPeopleIds, levelOptions } from "permissions/_views/grant-row/grant-row-model";
import HolderCell from "permissions/_views/grant-row/holder-cell";
import { type PersonNames, personNameState, removerNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { personReference, personStateText } from "permissions/_views/targets/permission-target-state";
import { useGrantChanges } from "permissions/_views/grant-row/use-grant-changes";
import AddPeopleDialog from "permissions/_views/records/add-people-dialog";
import CheckAccess from "permissions/_views/records/check-access";
import RecordLoadFailure from "permissions/_views/records/record-load-failure";
import {
  type RecordHolderRow,
  filterHolderRows,
  holderDisclosure,
} from "permissions/_views/records/record-grants-model";
import { hasAccessCheck, recordDomainOf } from "permissions/_views/records/record-permissions-path";
import { RECORD_GRANTS_PAGE_SIZE, type RecordGrants } from "permissions/_views/records/use-record-grants";
import type { PermissionGranteeOption } from "permissions/_views/search/use-permission-grantee-typeahead";
import {
  REMOVED_JUST_NOW,
  REMOVE_ACCESS,
  SET_LEVEL,
  accessibleNames,
  bulkAnnouncements,
  recordPage,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import type { AccessLevel } from "permissions/resources/permission-resource-types";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useMemo, useState } from "react";

/**
 * Who holds a grant on one record, and the controls to change it: Check access, Find a person,
 * + Add people, and Remove access or Set level for the rows chosen (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "The record page").
 *
 * The record page draws it under its header, and the record's own screen embeds the same panel in a
 * drawer. Rows lead with the person, one per person: the strongest live grant, with a disclosure to
 * the others when Messaging has written a derived grant beside a direct one. A person whose grants are
 * all removed keeps a row, labelled, so it can be granted again.
 */

/** Stays in view while rows are chosen, so the actions are never a scroll away from the rows. */
const BulkBar = styled(Paper)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 3,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
}));

function useIndividuals(rows: readonly RecordHolderRow[]): PersonNames {
  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const userIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      ids.add(row.granteeUserId.value);
      for (const grant of row.grants) for (const id of grantPeopleIds(grant)) ids.add(id);
    }
    return ids;
  }, [rows]);
  return usePersonNames(userIds);
}

/** A holder in a sentence: their name, or a phrase that reads in its place ("a former member"). */
function nameOf(names: PersonNames, userId: Guid): string {
  return personReference(personNameState(names, userId.value));
}

export type RecordAccessPanelProps = Readonly<{
  kind: PermissionKind;
  kinds: readonly PermissionKind[];
  recordId: Guid;
  /** The record's name, when the viewer may read it. */
  recordName?: string;
  grants: RecordGrants;
  pageIndex: number;
  onPageChange: (index: number) => void;
  /** An id prefix, so the page and a drawer over it never share element ids. */
  idPrefix: string;
  /** Leaves Check access out: a related record's group under a proposal links to its own page for that. */
  hideCheckAccess?: boolean;
}>;

export default function RecordAccessPanel(props: RecordAccessPanelProps) {
  const { kind, kinds, grants } = props;
  const noun = kind.targetNoun ?? "record";
  const domain = recordDomainOf(kind.resourceType);
  const sessionUser = useSessionUser();
  const viewerUserId = sessionUser?.id ?? Guid.EMPTY;
  const legalEntityId = sessionUser?.legalEntityId ?? Guid.EMPTY;
  const mayAdministerPlatform = sessionUser?.canAdministerPlatform() ?? false;
  const readOnly = !(sessionUser?.canGrantSigningAuthority() ?? false);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [disclosed, setDisclosed] = useState<ReadonlySet<string>>(new Set());
  const [levelMenu, setLevelMenu] = useState<HTMLElement | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [working, setWorking] = useState(false);
  // Each opening is a fresh dialog, keyed by this count, so it starts with only the people it is given.
  const [adding, setAdding] = useState<
    { key: number; people: readonly PermissionGranteeOption[]; accessLevel?: AccessLevel } | undefined
  >();

  const names = useIndividuals(grants.rows);
  const changes = useGrantChanges({ legalEntityId, nameOf: (userId) => nameOf(names, userId) }, viewerUserId);

  /** A grant as this page last knew it: one it removed is drawn removed at once, before the list is read again. */
  const current = (permission: Permission) => changes.removed.get(permission.id?.value ?? "") ?? permission;
  const model = (permission: Permission) =>
    buildGrantRow({
      kind,
      kinds,
      permission,
      viewerUserId,
      mayAdministerPlatform,
      // The template page refuses anybody without administration access.
      mayOpenTemplates: sessionUser?.canAccessAdministration() ?? false,
      grantor: personNameState(names, permission.grantorUserId.value),
      remover: removerNameState(names, permission),
    });

  const describe = (row: RecordHolderRow) => {
    const individual = names.individuals.get(row.granteeUserId.value.toLowerCase());
    return `${individual?.fullName ?? ""} ${individual?.email?.value ?? ""}`;
  };
  const rows = filterHolderRows(grants.rows, query, describe);

  /** The grants of a row the page may switch off: live, and not locked as derived or as the viewer's own. */
  const removable = (row: RecordHolderRow) =>
    row.grants.map(current).filter((grant) => !grant.isDisabled && model(grant).hasSwitch);
  const choosable = readOnly ? [] : rows.filter((row) => removable(row).length > 0);
  const chosen = choosable.filter((row) => selected.has(row.key));
  const bulkLevels = levelOptions(kind, {}, mayAdministerPlatform);

  const toggle = (key: string, on: boolean) =>
    setSelected((now) => {
      const next = new Set(now);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  // A person's access is removed whole: every grant of theirs the page may switch off.
  const removeChosen = async () => {
    setConfirmingRemoval(false);
    setWorking(true);
    let done = 0;
    for (const row of chosen) {
      let succeeded = true;
      for (const grant of removable(row)) if (!(await changes.remove(grant, kind, { quiet: true }))) succeeded = false;
      if (succeeded) done += 1;
    }
    setWorking(false);
    setSelected(new Set());
    enqueueSnackbar(bulkAnnouncements.removed(kind.name, done, chosen.length), {
      variant: done === chosen.length ? "success" : "warning",
      role: "status",
    });
  };

  // A level is the row's own grant's; a derived grant beside it keeps the level its parent gave.
  const setLevelOfChosen = async (option: (typeof bulkLevels)[number]) => {
    setLevelMenu(null);
    setWorking(true);
    let done = 0;
    for (const row of chosen) if (await changes.changeLevel(removable(row)[0], kind, option.level)) done += 1;
    setWorking(false);
    setSelected(new Set());
    enqueueSnackbar(bulkAnnouncements.changed(kind.name, option.label, done, chosen.length), {
      variant: done === chosen.length ? "success" : "warning",
      role: "status",
    });
  };

  const renderGrant = (row: RecordHolderRow, permission: Permission, isPrimary: boolean): ReactNode => {
    const shown = current(permission);
    const key = shown.id?.value ?? row.key;
    const state = personNameState(names, row.granteeUserId.value);
    const name = personReference(state);
    const built = model(shown);
    const rowModel = changes.removedJustNow.has(key) && shown.isDisabled ? { ...built, removedText: REMOVED_JUST_NOW } : built;
    const isChoosable = isPrimary && choosable.includes(row);
    const disclosure = isPrimary ? holderDisclosure(row, kinds) : undefined;
    const isOpen = disclosed.has(row.key);
    const subRowsId = `${props.idPrefix}-${row.key}-grants`;

    const select =
      readOnly || !isPrimary ? undefined : (
        <Checkbox
          checked={isChoosable && selected.has(row.key)}
          disabled={!isChoosable || working}
          onChange={(_, checked) => toggle(row.key, checked)}
          slotProps={{ input: { "aria-label": `Choose ${name}` } }}
        />
      );
    const header = isPrimary ? (
      <HolderCell
        userId={row.granteeUserId}
        individual={names.individuals.get(row.granteeUserId.value.toLowerCase())}
        state={state}
        onRetry={names.retry}
        select={select}
      >
        {disclosure && (
          <Button
            size="small"
            color="inherit"
            aria-expanded={isOpen}
            aria-controls={subRowsId}
            aria-label={`${accessibleNames.grants(kind.name, name)}: ${disclosure}`}
            onClick={() =>
              setDisclosed((now) => {
                const next = new Set(now);
                if (next.has(row.key)) next.delete(row.key);
                else next.add(row.key);
                return next;
              })
            }
            startIcon={<ChevronRightIcon sx={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />}
            sx={{ textTransform: "none", color: "text.secondary", p: 0, minWidth: 0 }}
          >
            {disclosure}
          </Button>
        )}
      </HolderCell>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ pl: { xs: 0, sm: 5 } }}>
        {personStateText(state) ?? name}
      </Typography>
    );

    return (
      <GrantRow
        key={key}
        row={rowModel}
        subject="record"
        personName={name}
        personHeader={header}
        pending={changes.pending.has(key)}
        alert={changes.alerts.get(key)}
        highlight={shown.id ? changes.highlighted.has(shown.id.value) : false}
        onSwitch={readOnly ? undefined : (enabled) => void (enabled ? changes.grantAgain(shown, kind) : changes.remove(shown, kind))}
        onChangeLevel={readOnly ? undefined : (level) => void changes.changeLevel(shown, kind, level)}
        onRetryGrantor={names.retry}
      />
    );
  };

  let body: ReactNode;
  if (grants.error) {
    body = <RecordLoadFailure title={recordPage.holdersFailed(noun)} detail={grants.error} onRetry={grants.retry} />;
  } else if (grants.isLoading) {
    body = (
      <Box aria-busy="true" aria-label="Loading holders" sx={{ display: "grid", gap: 1 }}>
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} variant="rectangular" height={56} />
        ))}
      </Box>
    );
  } else if (grants.rows.length === 0) {
    body = (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {`${recordPage.holdersCaption(noun, 0)}.`}
      </Typography>
    );
  } else if (rows.length === 0) {
    body = (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {recordPage.noMatch(query.trim())}
      </Typography>
    );
  } else {
    body = (
      <Paper variant="outlined">
        <GrantRowTable label={`Holders of ${kind.name}`}>
          {rows.flatMap((row) => {
            const drawn = [renderGrant(row, row.primary, true)];
            if (disclosed.has(row.key)) {
              drawn.push(
                <Box key={`${row.key}-grants`} id={`${props.idPrefix}-${row.key}-grants`} role="rowgroup" sx={{ display: "contents" }}>
                  {row.grants.slice(1).map((grant) => renderGrant(row, grant, false))}
                </Box>,
              );
            }
            return drawn;
          })}
        </GrantRowTable>
      </Paper>
    );
  }

  const allChosen = choosable.length > 0 && chosen.length === choosable.length;

  return (
    <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
      {domain && hasAccessCheck(domain) && !props.hideCheckAccess && (
        <CheckAccess
          domain={domain}
          recordId={props.recordId.value}
          noun={noun}
          onGiveAccess={readOnly ? undefined : (person, accessLevel) => setAdding({ key: Date.now(), people: [person], accessLevel })}
        />
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <TextField
          size="small"
          label={recordPage.findPerson}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ flex: "1 1 220px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" aria-hidden />
                </InputAdornment>
              ),
            },
          }}
        />
        {!readOnly && (
          <Button variant="contained" startIcon={<GroupAddIcon />} onClick={() => setAdding({ key: Date.now(), people: [] })}>
            {recordPage.addPeople}
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {recordPage.ownOrganizationOnly}
      </Typography>
      {choosable.length > 0 && (
        <BulkBar variant="outlined" role="toolbar" aria-label={`Change the chosen holders of this ${noun}`}>
          <Checkbox
            checked={allChosen}
            indeterminate={chosen.length > 0 && !allChosen}
            disabled={working}
            onChange={(_, checked) => setSelected(checked ? new Set(choosable.map((row) => row.key)) : new Set())}
            slotProps={{ input: { "aria-label": "Choose everyone listed" } }}
          />
          <Typography variant="body2" sx={{ flex: "1 1 auto" }}>
            {chosen.length > 0 ? bulkAnnouncements.selected(chosen.length) : "Choose people to change them together"}
          </Typography>
          <Button variant="outlined" color="error" startIcon={<EditOffIcon />} disabled={chosen.length === 0 || working} onClick={() => setConfirmingRemoval(true)}>
            {REMOVE_ACCESS}
          </Button>
          {bulkLevels.length > 1 && (
            <Button
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              disabled={chosen.length === 0 || working}
              aria-haspopup="menu"
              onClick={(event) => setLevelMenu(event.currentTarget)}
            >
              {SET_LEVEL}
            </Button>
          )}
        </BulkBar>
      )}
      {body}
      {grants.grantTotal > RECORD_GRANTS_PAGE_SIZE && (
        <TablePagination
          component="div"
          count={grants.grantTotal}
          page={props.pageIndex}
          rowsPerPage={RECORD_GRANTS_PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_, index) => {
            setSelected(new Set());
            props.onPageChange(index);
          }}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} grants`}
        />
      )}
      <Menu anchorEl={levelMenu} open={Boolean(levelMenu)} onClose={() => setLevelMenu(null)}>
        {bulkLevels.map((option) => (
          <MenuItem key={option.label} onClick={() => void setLevelOfChosen(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={confirmingRemoval} onClose={() => setConfirmingRemoval(false)} aria-labelledby={`${props.idPrefix}-remove-title`}>
        <DialogTitle id={`${props.idPrefix}-remove-title`}>{bulkAnnouncements.confirmRemoval(kind.name, chosen.length)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`${chosen.map((row) => nameOf(names, row.granteeUserId)).join(", ")}. ${bulkAnnouncements.removalUndo}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingRemoval(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void removeChosen()}>
            {REMOVE_ACCESS}
          </Button>
        </DialogActions>
      </Dialog>
      {adding && (
        <AddPeopleDialog
          key={adding.key}
          open
          kind={kind}
          targetId={props.recordId.value}
          recordName={props.recordName}
          initialPeople={adding.people}
          initialAccessLevel={adding.accessLevel}
          legalEntityId={legalEntityId}
          viewerUserId={viewerUserId}
          mayAdministerPlatform={mayAdministerPlatform}
          onClose={() => setAdding(undefined)}
        />
      )}
      {/* One polite region for every change this panel makes, in the wording module's words. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        <span key={changes.announcement.key}>{changes.announcement.text}</span>
      </Box>
    </Box>
  );
}
